import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import differenceBy from 'lodash/differenceBy'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import UserSelector from 'modules/core/components/UserSelector'
import i18n from 'modules/core/lib/i18n'
import type User from 'modules/core/store/models/User'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import type PermissionGroup from 'modules/permissions/store/models/PermissionGroup'

import useAddMembersToPermissionGroup from '../../../hooks/useAddMembersToPermissionGroup'

interface Props {
  onClose: () => void
  users: User[]
  permissionGroup: PermissionGroup
  contractVersions: ContractVersions
}

const AssignUsersModal = ({ users, permissionGroup, onClose, contractVersions }: Props) => {
  const [selectedIds, setSelectedIds] = React.useState<number[]>([])
  const [addMembers, addingMembers] = useAddMembersToPermissionGroup()

  const submitDisabled = !selectedIds.length || addingMembers

  const selectableUsers = differenceBy(users, permissionGroup.users, (user) => user.attributes.id)

  const onSelectUser = (id: number | null | undefined) => {
    if (!id) {
      setSelectedIds([])

      return
    }

    const newSelection = xor(selectedIds, [id])
    setSelectedIds(newSelection)
  }

  const onSelectEmployees = (ids: number[]) => {
    setSelectedIds(ids)
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    await addMembers({
      permissionGroupId: permissionGroup.id,
      accessIds: selectedIds,
    })
    closePortal()
  }

  return (
    <Modal
      illustration="add-user"
      title={i18n.t('settings.permissions.group.assign_employees.title')}
      description={i18n.t('settings.permissions.group.assign_employees.description')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Button submit disabled={submitDisabled}>
          {i18n.t('common.save')}
        </Button>
      }
    >
      <UserSelector
        users={selectableUsers}
        onSelect={onSelectUser}
        onSelectMultiple={onSelectEmployees}
        selectedIds={selectedIds}
        showDefaultFilters
        applyDefaultFilters
        contractVersions={contractVersions}
      />
    </Modal>
  )
}

export default observer(AssignUsersModal)
